import axios from '@/src/api/axios_init'
import { BaseButton } from '@/src/components/buttons/BaseButton'
import { Box } from '@chakra-ui/react'
import moment from 'moment'
import posthog from 'posthog-js'
import type { ReactNode } from 'react'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import { FlexCol, FlexRow } from '../components'
import { env } from '../env'
import { safeLocalStorage } from './safeStorage'
import { isProduction } from './utils'

type BoundaryType = 'global' | 'content'

interface IErrorBoundaryProps {
  children?: ReactNode
  boundaryType?: BoundaryType
}

const isDynamicImportError = (error: Error): boolean => {
  const errorText = `${error.message || ''} ${error.stack || ''}`
  return errorText.includes('dynamically imported module')
}

const ERROR_LOGGING_URL = '/tracing/trace-error'

function logErrorToService(error: Error, errorInfo: { componentStack: string }, boundaryType: BoundaryType = 'global') {
  const environment = isProduction() ? 'prod' : 'staging'
  const timestamp = moment()
  const humanDate = moment(timestamp).format('YYYY-MM-DD HH:mm:ss')
  const sessionData = JSON.parse(safeLocalStorage.getItem('store:session') || '{}')
  const user = sessionData.state?.user || null
  delete user?.numberOfShifts
  const url = window.location.href

  if (!env.isDev) {
    axios.post(ERROR_LOGGING_URL, {
      environment,
      timestamp,
      humanDate,
      url,
      user,
      error: error.stack,
      errorInfo,
      boundaryType,
    })

    posthog.capture(`Web app crash - ${boundaryType}`, {
      error_message: error.message,
      stack_trace: error.stack,
      error_info: errorInfo,
      user,
      boundary_type: boundaryType,
    })
  } else {
    console.error(`[ErrorBoundary - ${boundaryType}] Error:`, error, errorInfo)
  }
}

function ErrorFallback({
  error,
  resetErrorBoundary,
  boundaryType,
}: { error: Error; resetErrorBoundary: () => void; boundaryType?: BoundaryType }) {
  const messageToUser =
    boundaryType === 'global'
      ? 'Etwas ist schief gelaufen. Klicken Sie auf die folgende Schaltfläche, um den Cache zu bereinigen und automatisch neu zu laden.'
      : 'Der Inhalt konnte aufgrund eines unerwarteten Fehlers nicht geladen werden. Bitte warten Sie ein paar Sekunden und klicken Sie dann auf die Schaltfläche unten, um es erneut zu versuchen.'
  return (
    <FlexCol height={'100vh'} padding={'16px'} gap='8px' justifyContent='center' alignItems='center'>
      <FlexCol position='absolute' top='33%' gap='20px'>
        <h1 style={{ maxWidth: '530px', textAlign: 'center' }}>{messageToUser}</h1>
        <FlexRow justifyContent={'center'}>
          <Box>
            <BaseButton
              width={'300px'}
              variant='primary'
              size='big'
              text='Neu laden'
              onClick={() => {
                if (boundaryType === 'global') {
                  safeLocalStorage.clear()
                }
                resetErrorBoundary()
                setTimeout(() => {
                  // To give time for calls to finish, else user has to click twice.
                  resetErrorBoundary()
                }, 3000)
              }}
            />
          </Box>
        </FlexRow>
      </FlexCol>
    </FlexCol>
  )
}

const ErrorBoundary: React.FC<IErrorBoundaryProps> = ({ children, boundaryType = 'global' }) => {
  return (
    <ReactErrorBoundary
      FallbackComponent={props => <ErrorFallback {...props} boundaryType={boundaryType} />}
      onError={(error, errorInfo) => {
        logErrorToService(error, errorInfo, boundaryType)
        if (!env.isDev && isDynamicImportError(error)) window.location.reload()
      }}
    >
      {children}
    </ReactErrorBoundary>
  )
}

export default ErrorBoundary
